// Supabase Client Configuration
// Provides Supabase client instance for database operations

// Since @supabase/supabase-js may not be installed, we'll use a fallback approach
// For now, use environment variables or fallback to the same Supabase project
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://eximyshafkuyehkiotuf.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4aW15c2hhZmt1eWVoa2lvdHVmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUxODc0MDEsImV4cCI6MjA3MDc2MzQwMX0.bjrI5MO_WUfnI53UaQ_m8BS8LPh2et5OrqMKmWUvYzw';

// Dynamic import for Supabase client (handles case where it's not installed)
let supabase = null;

async function initSupabaseClient() {
  try {
    // Try to dynamically import @supabase/supabase-js
    const { createClient } = await import('@supabase/supabase-js');
    supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
    return supabase;
  } catch (error) {
    console.warn('@supabase/supabase-js not available, using fetch API instead');
    return null;
  }
}

// Initialize on module load
initSupabaseClient();

// Export getter function for Supabase client
export const getSupabaseClient = async () => {
  if (!supabase) {
    supabase = await initSupabaseClient();
  }
  return supabase;
};

// Export direct query function using fetch (fallback when Supabase SDK not available)
export const querySupabase = async (table, filters = {}, options = {}) => {
  try {
    // Use Supabase REST API directly via fetch
    const url = new URL(`${SUPABASE_URL}/rest/v1/${table}`);
    
    // Add filters as query parameters using PostgREST syntax
    // Format: column=operator.value (e.g., text_id=eq.value)
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Use proper PostgREST filter syntax: column=eq.value
        url.searchParams.append(`${key}`, `eq.${encodeURIComponent(value)}`);
      }
    });
    
    // Add order_by if specified (PostgREST uses 'order' parameter)
    // Format: order=column.direction
    if (options.order_by) {
      const orderDir = (options.order_direction || 'asc').toLowerCase();
      url.searchParams.append('order', `${options.order_by}.${orderDir}`);
    }
    
    // Add limit if specified
    if (options.limit) {
      url.searchParams.append('limit', options.limit.toString());
    }
    
    // Add offset if specified
    if (options.offset) {
      url.searchParams.append('offset', options.offset.toString());
    }
    
    // Select all columns by default
    url.searchParams.append('select', '*');
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Supabase query failed:', response.status, errorText);
      console.error('Request URL:', url.toString());
      throw new Error(`Supabase query failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return Array.isArray(data) ? data : [data];
  } catch (error) {
    console.error('Error querying Supabase:', error);
    throw error;
  }
};

export default supabase;

